import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { QuestionItem } from './components/questions';

function QuestionnaireComponent({ block, onComplete }) {
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [touched, setTouched] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    // Normalize and initialize questions for both questionnaire and scale blocks
    const normalizeType = (t) => {
      if (!t) return 'text';
      const map = {
        multiple_choice: 'single_choice',
        single_choice: 'single_choice',
        multiple_selection: 'multiple_selection',
        likert: 'likert',
        text: 'text',
        boolean: 'boolean',
        slider: 'slider',
      };
      return map[t] || 'text';
    };

    const toArray = (qs) => {
      if (!qs) return [];
      if (Array.isArray(qs)) return qs;
      // Object map -> array of values preserving original keys via _key
      return Object.entries(qs).map(([k, v]) => ({ _key: k, ...v }));
    };

    let rawQuestions = toArray(block.questions);
    let newQuestions = [];

    if (block.type === 'scale' && block.scale?.response_model) {
      const rm = block.scale.response_model;
      newQuestions = rawQuestions.map((item, idx) => ({
        id: item?.id ?? item?._key ?? String(idx),
        displayIndex: idx,
        text: item?.text ?? '',
        type: rm.type,
        range: rm.range ?? [0, 1],
        labels: rm.labels ?? [],
        defaultValue: rm.default_value ?? (Array.isArray(rm.range) ? Math.round((rm.range[0] + rm.range[1]) / 2) : 0),
      }));
    } else {
      newQuestions = rawQuestions.map((q, idx) => {
        if (!q) return null;
        const type = normalizeType(q.type);
        const id = q.id ?? q?._key ?? String(idx);
        const text = q.text ?? '';
        const options = Array.isArray(q.options) ? q.options : [];

        // helpers for likert numeric detection
        const numericOpts = options.map((o) => Number(o));
        const allNumeric = options.length > 0 && numericOpts.every((n) => !isNaN(n));

        if (type === 'likert') {
          let range, defaultValue;
          if (allNumeric) {
            const min = Math.min(...numericOpts);
            const max = Math.max(...numericOpts);
            range = [min, max];
            defaultValue = Math.round((min + max) / 2);
          } else {
            range = [0, Math.max(0, options.length - 1)];
            defaultValue = Math.round((range[0] + range[1]) / 2);
          }
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options,
            range,
            labels: options,
            likertConfig: q.likertConfig || null,
            defaultValue,
            required: !!q.required,
          };
        }

        if (type === 'text') {
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options: [],
            range: [],
            labels: [],
            defaultValue: '',
            required: !!q.required,
          };
        }

        if (type === 'boolean') {
          const boolOpts = options.length === 2 ? options : ['Yes', 'No'];
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options: boolOpts,
            range: [],
            labels: boolOpts,
            defaultValue: '',
            required: !!q.required,
          };
        }

        if (type === 'single_choice') {
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options,
            range: [],
            labels: options,
            defaultValue: '',
            required: !!q.required,
          };
        }

        if (type === 'multiple_selection') {
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options,
            range: [],
            labels: options,
            defaultValue: [],
            selectionConfig: q.multipleSelection || null,
            required: !!q.required,
          };
        }

        if (type === 'slider') {
          const cfg = q.slider || {};
          const min = Number(cfg.min ?? 0);
          const max = Number(cfg.max ?? 100);
          const step = Number(cfg.step ?? 1);
          const labels = Array.isArray(cfg.labels) ? cfg.labels : [];
          return {
            id,
            displayIndex: idx,
            type,
            text,
            options: [],
            range: [min, max],
            step,
            labels,
            defaultValue: Math.round((min + max) / 2),
            required: !!q.required,
          };
        }

        // Fallback: treat any unknown type as a text input to avoid crashes
        console.warn('Unknown question type, falling back to text:', q.type);
        return {
          id,
          displayIndex: idx,
          type: 'text',
          text,
          options: [],
          range: [],
          labels: [],
          defaultValue: '',
          required: !!q.required,
        };
      }).filter(Boolean);
    }

    const initialResponses = {};
    newQuestions.forEach((q) => {
      if (q.type === 'multiple_selection') {
        initialResponses[q.id] = Array.isArray(q.defaultValue) ? q.defaultValue : [];
      } else {
        initialResponses[q.id] = q.defaultValue ?? '';
      }
    });

    setQuestions(newQuestions);
    setResponses(initialResponses);
    setTouched({});
    setShowValidation(false);
    setSubmitting(false);
  }, [block]);

  const handleChange = (questionID, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionID]: value,
    }));
    setTouched((prev) => ({ ...prev, [questionID]: true }));
  };

  // All items are mandatory: every question must be touched by the user
  const allAnswered = questions.length > 0 && questions.every((q) => {
    if (!touched[q.id]) return false;
    const val = responses[q.id];
    // For text inputs, require non-empty
    if (q.type === 'text' || q.type === 'textarea') return typeof val === 'string' && val.trim().length > 0;
    // For boolean/single_choice/radio, require a selection
    if (q.type === 'boolean' || q.type === 'single_choice' || q.type === 'radio') return val !== undefined && val !== '';
    // For multiple_selection/checkbox, require at least one selected
    if (q.type === 'multiple_selection' || q.type === 'checkbox') return Array.isArray(val) && val.length > 0;
    // For likert/slider/numeric: touched is sufficient (they always have a numeric value)
    return true;
  });

  const handleSubmit = () => {
    if (!allAnswered) {
      setShowValidation(true);
      return;
    }
    setSubmitting(true);
    console.log('Submitting responses');
    onComplete(responses);
  };

  const formatItem = (question, text) => {
    if (block.type === 'scale' && block.scale?.prompt_templates?.item_format) {
      return block.scale.prompt_templates.item_format
        .replace('{item_id}', question?.id ?? '')
        .replace('{text}', text);
    } else if (block.type === 'questionnaire') {
      // Prefer displayIndex for numbering. If unavailable, just return text.
      if (typeof question?.displayIndex === 'number') {
        return `${question.displayIndex + 1}. ${text}`;
      }
      return text;
    }
    return text;
  };

  const renderQuestionInput = (question) => {
    const { id, text } = question;
    const formattedText = formatItem(question, text);
    return (
      <Box key={id} sx={{ marginBottom: 2, width: '100%' }}>
        <Typography variant="body1" gutterBottom align="left">
          {formattedText}
        </Typography>
        <QuestionItem
          question={question}
          value={responses[id]}
          onChange={(v) => handleChange(id, v)}
        />
      </Box>
    );
  };

  return (
    <Container maxWidth="sm" sx={{ backgroundColor: '#fff', padding: 2, boxShadow: 1, borderRadius: 1, alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="h6" align="center" gutterBottom>
        {block.title ?? block.scale?.title ?? ''}
      </Typography>
      <Box sx={{ marginBottom: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {questions.map((question) => renderQuestionInput(question))}
      </Box>
      {showValidation && !allAnswered && (
        <Typography variant="body2" color="error" align="center" sx={{ mt: 1 }}>
          Please answer all items before submitting.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Box>
    </Container>
  );
}

export default QuestionnaireComponent;
